import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { McticHttpClient, McticHttpParams, Page, Pageable } from '@mctic/ng-core';
import { Observable } from 'rxjs';
import { ConsultaProdutoFiltro } from '../gerir-produto/consulta-produto/consulta-produto-filtro.model';
import { ConsultaProdutoPleito, DadosBasicosProduto } from '../shared/model';
import { AnaliseDadosBasicosProdutoVo } from '../shared/model/analise-dados-basicos-produto-vo';
import { CadastroProdutoVO } from '../shared/model/cadastro-produto-vo.model';
import { ConsultaProdutoVO } from '../shared/model/consulta-produto-vo.model';
import { DadosBasicosProdutoVo } from '../shared/model/dados-basicos-produto-vo.model';
import { ProdutoPleitoVO } from '../shared/model/produto-pleito-vo.model';

@Injectable({
  providedIn: 'root'
})
export class ProdutoService {

  private readonly service = '/sepin-backend/rest/pleitos';

  constructor(private readonly http: McticHttpClient) {
  }

  obterProdutos(filtro: ConsultaProdutoFiltro, pageable: Pageable): Observable<Page<ConsultaProdutoPleito>> {
    const httpParams: McticHttpParams = new McticHttpParams();
    httpParams.set('ncm', filtro.ncm);
    httpParams.set('empresa', filtro.empresa);
    httpParams.set('cnpj', filtro.cnpj);
    httpParams.setPageable(pageable);

    if (filtro.produto) {
      httpParams.set('nome', filtro.produto);
    }
    if (filtro.situacao) {
      httpParams.set('situacao', filtro.situacao);
    }

    const params: HttpParams = httpParams.toHttpParams();
    return this.http.get<Page<ConsultaProdutoPleito>>(`${this.service}/${filtro.id}/dados-basicos-produto`, {params});
  }

  obterPleitoProdutos(idPleito: number, pageable: Pageable): Observable<Page<ConsultaProdutoVO>> {
    const httpParams: McticHttpParams = new McticHttpParams();
    httpParams.setPageable(pageable);
    const params: HttpParams = httpParams.toHttpParams();
    return this.http.get<Page<ConsultaProdutoVO>>(
      `${this.service}/${idPleito}/dados-basicos-produto/completo`, {params});
  }

  obterDadosBasicosPorId(idPleito: number, idProduto: number): Observable<DadosBasicosProduto> {
    return this.http.get<DadosBasicosProduto>(`${this.service}/${idPleito}/dados-basicos-produto/${idProduto}`);
  }

  obterPorPleito(idPleito: number, pageable: Pageable): Observable<Page<ProdutoPleitoVO>> {
    const queryParams = new McticHttpParams().setPageable(pageable);
    return this.http.get<Page<ProdutoPleitoVO>>(`${this.service}/${idPleito}/dados-basicos-produto/list`, {params: queryParams.toHttpParams()});
  }

  obterPorNome(idPleito: number, nome: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.service}/${idPleito}/dados-basicos-produto/nome/autocomplete/${nome}`);
  }

  cadastrarDadosBasicosProduto(idPleito: number, cadastro: DadosBasicosProduto, anexos, arquivos: File[]): Observable<DadosBasicosProdutoVo> {
    const headers = new HttpHeaders();
    headers.append('Content-Type', null);

    const formData: FormData = new FormData();
    formData.append(
      'cadastroProduto',
      new Blob([JSON.stringify(cadastro)], {
        type: 'application/json'
      })
    );
    formData.append(
      'anexosDadosBasicosProduto',
      new Blob([JSON.stringify(anexos)], {
      type: 'application/json'
    }));
    arquivos.forEach((arquivo: File) => {
      formData.append('arquivos', arquivo);
    });
    return this.http.post<DadosBasicosProdutoVo>(`${this.service}/${idPleito}/dados-basicos-produto/cadastrar`, formData, {headers});
  }

  cancelar(idPleito: number, idProduto: number): Observable<void> {
    return this.http.delete<void>(`${this.service}/${idPleito}/dados-basicos-produto/${idProduto}`);
  }

  responder(idPleito: number, idProduto: number, cadastro: CadastroProdutoVO): Observable<void> {
    return this.http.put<void>(`${this.service}/${idPleito}/dados-basicos-produto/${idProduto}/responder`, cadastro);
  }

  removerAnexo(idPleito: number, i: string): Observable<void> {
    return this.http.delete<void>(`${this.service}/${idPleito}/dados-basicos-produto/anexos-dados-basicos/${i}`);
  }

  verificaMaxProdutos(idPleito: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.service}/${idPleito}/dados-basicos-produto/max-produtos`);
  }

  salvarAnalise(idProduto: number, analiseProduto: AnaliseDadosBasicosProdutoVo): Observable<AnaliseDadosBasicosProdutoVo> {
    const formData: FormData = new FormData();
    formData.append(
      'analiseProduto',
      new Blob([JSON.stringify(analiseProduto)], {
        type: 'application/json'
      })
    );
    return this.http.post<AnaliseDadosBasicosProdutoVo>(`${this.service}/${idProduto}/analise-dados-basicos`, formData);
  }

  buscarAnalises(idProduto: number): Observable<AnaliseDadosBasicosProdutoVo[]> {
    return this.http.get<AnaliseDadosBasicosProdutoVo[]>(`${this.service}/${idProduto}/analise-dados-basicos`);
  }

  buscarAnalisesPorTipo(idProduto: number, tipoExigencia: number): Observable<AnaliseDadosBasicosProdutoVo[]> {
    return this.http.get<AnaliseDadosBasicosProdutoVo[]>(`${this.service}/${idProduto}/analise-dados-basicos/tipo/${tipoExigencia}`);
  }

  responderExigencia(idProduto: number, analise: AnaliseDadosBasicosProdutoVo): Observable<AnaliseDadosBasicosProdutoVo> {
    return this.http.put<AnaliseDadosBasicosProdutoVo>(`${this.service}/${idProduto}/analise-dados-basicos/responder-exigencia`, analise);
  }

  removerProduto(idPleito: number, idProduto: number): Observable<void> {
    return this.http.delete(`${this.service}/${idPleito}/dados-basicos-produto/${idProduto}`);
  }

  salvarMotivoIndeferimento(idPleito: number, idProduto: number, dados: DadosBasicosProdutoVo ): Observable<DadosBasicosProdutoVo> {
    return this.http.put<DadosBasicosProdutoVo>(`${this.service}/${idPleito}/dados-basicos-produto/${idProduto}/motivar-indeferimento`, dados);
  }

  cancelarProduto(idPleito: number, idProduto: number): Observable<void> {
    return this.http.put<void>(`${this.service}/${idPleito}/dados-basicos-produto/${idProduto}/cancelar`, {});
  }

  obterDadosProdutoComercializado(idPleito: number, idProduto: number): Observable<DadosBasicosProduto> {
    return this.http.get<DadosBasicosProduto>(`${this.service}/${idPleito}/dados-basicos-produto/${idProduto}/produto-comercializado`);
  }

  salvarComercializadoAcessorios(idPleito: number, dados: DadosBasicosProduto): Observable<void> {
    return this.http.post<void>(`${this.service}/${idPleito}/dados-basicos-produto/produto-comercializado`, dados);
  }

}

